import { FaArrowRight, FaInfoCircle } from "react-icons/fa";
import { CalculateResultCardStyled } from "../CotermWorkStyled";
import {
  CALCULATE_TYPE,
  CotermCalculateType,
} from "../SubscriptonCotermWorkModal";
import {
  differenceInDays,
  getAccountTimeFormatDate,
  getUtcDate,
} from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useAddChannelCalculator } from "../useAddChannelCalculator";
import { usePurchaseCalculator } from "../usePurchaseCalculator";
import { useMemo } from "react";
import { WithTooltip } from "components/blocks/WithTooltip";

type Props = {
  tabType: CotermCalculateType;
  addChannelCalc: ReturnType<typeof useAddChannelCalculator>;
  purchaseCalc: ReturnType<typeof usePurchaseCalculator>;
};

export default function CalculatedResultCard({
  tabType,
  addChannelCalc,
  purchaseCalc,
}: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { currentCalculatedResult, currentCalculatedResultNullMessage } =
    addChannelCalc;
  const { purchaseCalculations, inputs: purchaseInputs } = purchaseCalc;

  const result = useMemo(() => {
    return tabType === CALCULATE_TYPE.CHANNEL
      ? currentCalculatedResult
      : purchaseCalculations!;
  }, [tabType, currentCalculatedResult, purchaseCalculations]);

  const getYearToString = (year: number) => {
    return `${year} Year${year > 1 ? "s" : ""} (${year * 365} days)`;
  };

  return (
    <CalculateResultCardStyled.Card>
      <div className="title">
        <h2>
          <FaArrowRight />
          Calculated Results
        </h2>
        {tabType === CALCULATE_TYPE.PURCHASE && (
          <span className="desc">Subscription after purchase</span>
        )}
      </div>

      {tabType === CALCULATE_TYPE.CHANNEL &&
        result === null &&
        currentCalculatedResultNullMessage && (
          <div className="null-message">
            <strong>{currentCalculatedResultNullMessage}</strong>
          </div>
        )}

      {result?.newExpirationDate && result.newNumberOfCameras !== null && (
        <ul className="items">
          <li>
            <div className="item-title">New Number of Activated Channels</div>
            <div className="value">{result.newNumberOfCameras}</div>
          </li>
          <li>
            <div className="item-title">Co-Termination Date</div>
            <div className="value">
              {getAccountTimeFormatDate(String(result.newExpirationDate), {
                ...selectedAccount,
                timezone: "UTC",
              })}
              {tabType === CALCULATE_TYPE.CHANNEL && currentCalculatedResult && (
                <>
                  {currentCalculatedResult.isCapped &&
                    currentCalculatedResult.maxExpireDate && (
                      <WithTooltip
                        tooltip={
                          <div>
                            Max Expiration Date:{" "}
                            {getAccountTimeFormatDate(
                              String(currentCalculatedResult.maxExpireDate),
                              { ...selectedAccount, timezone: "UTC" }
                            )}
                          </div>
                        }
                      >
                        <FaInfoCircle size={16} />
                      </WithTooltip>
                    )}
                </>
              )}
            </div>
            {tabType === CALCULATE_TYPE.CHANNEL && currentCalculatedResult && (
              <span className="desc">
                Today <strong>+ {currentCalculatedResult.unitDays} days</strong>{" "}
                - 1 Day (For Today)
              </span>
            )}
            {tabType === CALCULATE_TYPE.PURCHASE &&
              result.newNumberOfCameras !== 0 &&
              (purchaseCalculations.isCapped ? (
                <span className="desc">
                  <strong style={{ display: "inline-flex", gap: 6 }}>
                    Capped at Subscription Key Expiration Date
                    <WithTooltip
                      tooltip={
                        <div>
                          Current Expiration +{" "}
                          {getYearToString(purchaseInputs.duration)} ={" "}
                          {getAccountTimeFormatDate(
                            String(
                              purchaseCalculations.subscriptionKeyExpirationDate
                            ),
                            { ...selectedAccount, timezone: "UTC" }
                          )}
                        </div>
                      }
                    >
                      <FaInfoCircle size={16} />
                    </WithTooltip>
                  </strong>
                </span>
              ) : (
                <span className="desc">
                  <div>Current Expiration Date:</div>
                  <strong>
                    +{" "}
                    {Math.ceil(
                      purchaseCalculations.newExpirationDays -
                        differenceInDays(
                          purchaseInputs.expirationDate!,
                          getUtcDate(new Date())
                        )
                    )}{" "}
                    days
                  </strong>{" "}
                  {/* <span>
                   - 1 Day
                    (For Today)
                  </span>  */}
                </span>
              ))}
          </li>
        </ul>
      )}
    </CalculateResultCardStyled.Card>
  );
}
